/**
 * D4 · চেকআউট — `/checkout`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame D4 (360 × 680).
 *
 * Frame note: "The screen the whole flow exists for. Every fee on its own line, the
 * total boxed, and the amount repeated inside the button so the commitment is
 * unambiguous."
 *
 * This screen computes nothing. `quote.fees` is rendered in the order the server sent
 * it, whatever it contains; the total printed is `quote.total.display`, not a sum taken
 * here; and the button's sentence — `৳ 1,190 দিয়ে নিশ্চিত করুন` — arrives as one string,
 * because it is drawn as one and because a client that builds a Bangla sentence around a
 * number is composing Bangla (docs/05 § conventions).
 * `assertCheckoutQuoteRenderable` throws if the server's own numbers disagree, because a
 * checkout that silently repairs a bad quote is a checkout that can show a total nobody
 * agreed to.
 *
 * DEVIATION: the frame's own border is `1.5px solid #0E3A45` where every other P0 frame
 * is `1px solid #E2DED5`. Transcribed as drawn — the harness captures the frame element,
 * so its border is part of the screen — but it reads like canvas emphasis on the flow's
 * key screen rather than a UI decision. Reported.
 *
 * DEVIATION: the partner disclosure is added. D4 draws none, and it is a money screen.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@oprappo/ui';
import type { CheckoutQuoteResponse } from '@oprappo/contracts';
import { assertCheckoutQuoteRenderable } from '@oprappo/contracts';
import { Frame, PartnerDisclosure, ScreenHeader } from '../offering/chrome.js';
import { Loaded, commerce, useResource } from '../offering/commerce.js';

/** `padding:11px 0;border-top:1px solid #E2DED5` — one fee line. */
function FeeRow({ labelBn, amount }: { labelBn: string; amount: string }): JSX.Element {
  return (
    <div className="py-[11px] border-t border-border flex justify-between items-baseline">
      <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2">{labelBn}</div>
      <div className="font-manrope font-medium text-[15px] leading-[1] text-ink tabular-nums">
        {amount}
      </div>
    </div>
  );
}

export function D4CheckoutView({ quote }: { quote: CheckoutQuoteResponse }): JSX.Element {
  const navigate = useNavigate();
  const q = assertCheckoutQuoteRenderable(quote);
  const d = q.destination;

  return (
    <Frame className="bg-sand border-[1.5px] border-teal">
      <ScreenHeader titleBn="চেকআউট" onBack={() => navigate(-1)} />

      <div className="flex-1 p-[16px] oprappo-scroll">
        {/*
         * Both cards come from the destination the cart is bound for. Null means this
         * cart has no address yet, which D4 draws no state for — the cards are omitted
         * rather than drawn empty, and the gap is in docs/bangla-needed/commerce.md.
         */}
        {d === null ? null : (
          <>
            <div className="bg-white border border-border p-[13px] mb-[11px]">
              <div className="flex justify-between items-baseline">
                <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted">
                  {d.where_label_bn}
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/account/addresses')}
                  className="font-anek font-medium text-[13.5px] leading-[1.75] text-link bg-transparent border-0 p-0 rounded-none"
                >
                  {d.change_label_bn}
                </button>
              </div>
              <div className="font-anek font-medium text-[15px] leading-[1.75] text-ink mt-[6px]">
                {d.address_lines_bn[0]}
                <br />
                {d.address_lines_bn[1]}
              </div>
            </div>

            <div className="bg-white border border-border p-[13px] mb-[18px]">
              <div className="flex justify-between items-baseline">
                <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted">
                  {d.when_label_bn}
                </div>
                <button
                  type="button"
                  className="font-anek font-medium text-[13.5px] leading-[1.75] text-link bg-transparent border-0 p-0 rounded-none"
                >
                  {d.change_label_bn}
                </button>
              </div>
              <div className="font-anek font-medium text-[15px] leading-[1.75] text-ink mt-[6px]">
                {d.slot_bn}
              </div>
            </div>
          </>
        )}

        <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink mb-[11px]">
          {q.fees_heading_bn}
        </div>
        <div>
          <FeeRow labelBn={q.subtotal_label_bn} amount={q.subtotal.display} />
          {/* Exactly the rows the server returned, in the order it returned them. */}
          {q.fees.map((f) => (
            <FeeRow key={f.label_bn} labelBn={f.label_bn} amount={f.amount.display} />
          ))}
          <div className="py-[13px] border-t-2 border-b-2 border-teal flex justify-between items-center">
            <div className="font-anek font-semibold text-[17px] leading-[1.75] text-ink">
              {q.total_label_bn}
            </div>
            <div className="font-manrope font-bold text-[22px] leading-[1] text-ink tabular-nums">
              {q.total.display}
            </div>
          </div>
        </div>

        <div className="font-anek font-normal text-[13px] leading-[1.75] text-body mt-[13px]">
          {q.disclosure_bn}{' '}
          <button
            type="button"
            onClick={() => navigate(`/policy/refund/${q.refund_policy_key}`)}
            className="font-anek font-medium text-link border-b border-border-cool bg-transparent p-0 rounded-none"
          >
            {q.refund_link_bn}
          </button>
        </div>

        {/* Added — see the file header. The frame draws no partner on this screen. */}
        <PartnerDisclosure partnerName={q.partner_name} className="mt-[13px]" />
      </div>

      <div className="bg-white border-t border-border shadow-bottombar px-[16px] py-[13px]">
        {/*
         * Drawn as one string with the amount inside it: `৳ 1,190 দিয়ে নিশ্চিত করুন`.
         * The server composes it — this is the only place in flow D where an amount sits
         * inside a Bangla sentence, and it is drawn that way.
         */}
        <Button onClick={() => navigate('/checkout/payment')}>{q.confirm_label_bn}</Button>
      </div>
    </Frame>
  );
}

export function D4Checkout(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => commerce.quote(signal), []);
  const [resource] = useResource(load, 'quote');

  return <Loaded resource={resource}>{(quote) => <D4CheckoutView quote={quote} />}</Loaded>;
}
